import { useQueries } from "@tanstack/react-query";
import { Fragment, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { documentQueryKeys, useUpdateStar, useStars } from "../../api/documents";
import { collectionPath, useCollections } from "../../api/queries";
import { rpc } from "../../api/rpc";
import type { Doc, Star } from "../../api/types";
import { cx } from "../../lib/cx";
import { indexForPosition } from "../../lib/fractionalIndex";
import { Menu } from "../../ui/Menu";
import { useToast } from "../../ui/toast-context";

/**
 * The starred section. `stars.list` returns the stars only — the records they
 * point at are fetched per star through the ordinary `documents.info` cache, so
 * a document already on screen costs no extra request.
 *
 * Order is a fractional index the client computes, which is why a reorder is
 * one `stars.update` and never a renumbering of the list.
 */

export interface StarredRow {
  star: Star;
  title: string;
  url: string;
  icon: string | null;
}

export function starIndexFor(stars: readonly Star[], from: number, to: number): string {
  const keys = stars.map((star) => star.index);
  const without = [...keys];
  without.splice(from, 1);
  return indexForPosition(without, to > from ? to - 1 : to);
}

export function StarredSection({
  enabled,
  onUnstar,
}: {
  enabled: boolean;
  onUnstar: (star: Star) => void;
}): ReactNode {
  const stars = useStars(enabled);
  const collections = useCollections(enabled);
  const update = useUpdateStar();
  const toast = useToast();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const list = stars.data?.stars ?? [];
  const documentIds = list.map((star) => star.documentId).filter((id): id is string => id !== null);

  const documents = useQueries({
    queries: documentIds.map((id) => ({
      queryKey: documentQueryKeys.info(id),
      enabled,
      retry: false,
      queryFn: async () => {
        const result = await rpc<Doc>("documents.info", { id });
        return { document: result.data, abilities: {} };
      },
    })),
  });

  const titles = new Map<string, Doc>();
  documents.forEach((query, position) => {
    const id = documentIds[position];
    const data = query.data as { document: Doc } | undefined;
    if (id !== undefined && data !== undefined) titles.set(id, data.document);
  });

  const rows: StarredRow[] = list.map((star) => {
    if (star.documentId !== null) {
      const document = titles.get(star.documentId);
      return {
        star,
        title: document === undefined ? "…" : document.title === "" ? "Untitled" : document.title,
        url: document?.url ?? `/doc/${star.documentId}`,
        icon: document?.icon ?? "📄",
      };
    }
    const collection = (collections.data ?? []).find((entry) => entry.id === star.collectionId);
    return {
      star,
      title: collection?.name ?? "…",
      url: collection === undefined ? "/home" : collectionPath(collection),
      icon: collection?.icon ?? "📁",
    };
  });

  // The section simply is not there when nothing is starred, and it does not
  // announce itself while it loads: a heading over an empty list is noise.
  if (!enabled || rows.length === 0) return null;

  const reorder = (to: number): void => {
    const from = dragIndex;
    setDragIndex(null);
    setDropIndex(null);
    if (from === null || to === from || to === from + 1) return;
    const star = list[from];
    if (star === undefined) return;
    update.mutate(
      { id: star.id, index: starIndexFor(list, from, to) },
      {
        onError: (error) => {
          toast.show(error.message, "error");
        },
      },
    );
  };

  const zone = (position: number, name: string): ReactNode => (
    <li
      key={`zone-${String(position)}`}
      role="separator"
      aria-label={name}
      onDragOver={(event) => {
        event.preventDefault();
        setDropIndex(position);
      }}
      onDrop={(event) => {
        event.preventDefault();
        reorder(position);
      }}
      className={cx("-my-px h-1 rounded", dropIndex === position ? "bg-accent" : "bg-transparent")}
    />
  );

  return (
    <section aria-labelledby="sidebar-starred">
      <h2
        id="sidebar-starred"
        className="mb-1 px-2 text-xs font-semibold tracking-wide text-muted uppercase"
      >
        Starred
      </h2>
      <ul className="flex flex-col">
        {rows.map((row, position) => (
          <Fragment key={row.star.id}>
            {zone(position, `Drop before ${row.title}`)}
            <li
              draggable
              onDragStart={() => {
                setDragIndex(position);
              }}
              onDragEnd={() => {
                setDragIndex(null);
                setDropIndex(null);
              }}
              className="flex items-center gap-1 rounded-md"
            >
              <NavLink
                to={row.url}
                className={({ isActive }) =>
                  cx(
                    "flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1 text-sm",
                    isActive
                      ? "bg-hover font-medium text-ink"
                      : "text-muted hover:bg-hover hover:text-ink",
                  )
                }
              >
                <span aria-hidden="true" className="w-4 shrink-0 text-center">
                  {row.icon}
                </span>
                <span className="truncate">{row.title}</span>
              </NavLink>
              <Menu
                buttonLabel={`Actions for starred ${row.title}`}
                menuLabel="Starred actions"
                trigger="⋯"
                items={[
                  {
                    label: "Unstar",
                    onSelect: () => {
                      onUnstar(row.star);
                    },
                  },
                ]}
              />
            </li>
          </Fragment>
        ))}
        {zone(rows.length, "Drop at the end of Starred")}
      </ul>
    </section>
  );
}
